// Data profile of a table: per-column quality and a list of issues, used by
// the data table header, the summary chips and the Issues tab. Large tables
// are profiled in the data worker in chunks (see data/host.js), so every
// scan here can run over a range of rows.
import { columnLetter, inferType, isBlank, toDate, toNumber, valueKey } from "../../../engine";
import { displayValue } from "./display";

const ISSUE_LIMIT = 500;

// "null" (no value), "empty" (blank text), "mismatch" (doesn't fit the
// column type) or "ok".
export const cellState = (value, type) => {
  if (value === null || value === undefined) return "null";
  if (typeof value === "string" && value.trim() === "") return "empty";
  if (type === "number" && toNumber(value) === null) return "mismatch";
  if (type === "date" && toDate(value) === null) return "mismatch";
  return "ok";
};

const rowKey = (row, names) => names.map((name) => valueKey(row[name])).join("\u0001");

// Profiles rows in ranges: add(from, to) any number of times, then result().
export const createProfiler = (table, { warnings = [] } = {}) => {
  const names = table.columns.map((c) => c.name);
  const head = table.rows.slice(0, 1000);
  const columns = table.columns.map((column, index) => ({
    name: column.name,
    letter: column.letter || columnLetter(index),
    type: column.type || inferType(head.map((row) => row[column.name])),
    distinct: new Set(),
    ok: 0,
    null: 0,
    empty: 0,
    mismatch: 0,
  }));
  const issues = [];
  const seen = new Map();
  const duplicateRows = new Set();

  const add = (from, to) => {
    for (let rowIndex = from; rowIndex < to; rowIndex++) {
      const row = table.rows[rowIndex];
      columns.forEach((column) => {
        const value = row[column.name];
        const state = cellState(value, column.type);
        column[state] += 1;
        if (!isBlank(value)) column.distinct.add(valueKey(value));
        if (state === "mismatch" && issues.length < ISSUE_LIMIT) {
          issues.push({ kind: "typeError", column: column.name, row: rowIndex, value, message: `isn't a valid ${column.type}` });
        }
      });
      const key = rowKey(row, names);
      if (seen.has(key)) {
        duplicateRows.add(rowIndex);
        duplicateRows.add(seen.get(key));
      } else {
        seen.set(key, rowIndex);
      }
    }
  };

  const result = () => {
    const done = columns.map(({ distinct, ...column }) => ({ ...column, distinct: distinct.size, total: table.rows.length }));
    return {
      columns: done,
      issues,
      duplicateRows,
      truncated: false,
      counts: {
        rows: table.rows.length,
        columns: table.columns.length,
        nulls: done.reduce((sum, c) => sum + c.null, 0),
        empties: done.reduce((sum, c) => sum + c.empty, 0),
        duplicates: duplicateRows.size,
        warnings: warnings.length,
        typeErrors: done.reduce((sum, c) => sum + c.mismatch, 0),
      },
    };
  };

  return { add, result };
};

export const profileTable = (table, options) => {
  const profiler = createProfiler(table, options);
  profiler.add(0, table.rows.length);
  return profiler.result();
};

// Whether a row matches a summary-chip filter.
export const matchesFilter = (table, profile, filter, index) => {
  const row = table.rows[index];
  if (filter === "duplicates") return profile.duplicateRows.has(index);
  const wanted = filter === "nulls" ? "null" : filter === "empties" ? "empty" : "mismatch";
  return profile.columns.some((c) => cellState(row[c.name], c.type) === wanted);
};

// Rows matching a summary-chip filter.
export const rowsMatching = (table, profile, filter) => {
  if (!filter) return null;
  const indexes = [];
  for (let index = 0; index < table.rows.length; index++) if (matchesFilter(table, profile, filter, index)) indexes.push(index);
  return indexes;
};

// Whether any cell of the row shows text containing `query` (lower case).
export const matchesSearch = (table, query, index) => {
  const row = table.rows[index];
  return table.columns.some((c) => displayValue(row[c.name]).text.toLowerCase().includes(query));
};

// Turns rendered output sheet rows (header first) into a table for display.
export const sheetToTable = (sheet, writeHeader) => {
  const header = writeHeader ? sheet.rows[0] || [] : [];
  const body = writeHeader ? sheet.rows.slice(1) : sheet.rows;
  const width = Math.max(header.length, ...body.slice(0, 200).map((r) => r.length), 0);
  // Header names can repeat, so columns are keyed by position; `label` is shown.
  const columns = Array.from({ length: width }, (_, i) => ({
    name: `${i}`,
    letter: columnLetter(i),
    label: header[i] ? String(header[i]) : undefined,
  }));
  return {
    columns,
    rows: body.map((row) => Object.fromEntries(columns.map((c, i) => [c.name, row[i] ?? null]))),
  };
};
